import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLang } from "@/i18n";
import api, { extractErrorMessage } from "@/lib/api";
import PublicLayout from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { CalendarCheck, Loader2 } from "lucide-react";

function fmtLocal(iso, tz) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: tz, dateStyle: "medium", timeStyle: "short",
    }).format(new Date(iso));
  } catch { return iso; }
}

const detectTz = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "UTC"; }
};

const isoMidnight = (date) => {
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0);
  return d.toISOString();
};

export default function PublicBookingPage() {
  const { hostSlug, mtId } = useParams();
  const { t } = useLang();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [copied, setCopied] = useState(false);
  const tz = useMemo(detectTz, []);

  // Default to "today → +14 days".
  const [fromDate, setFromDate] = useState(() => isoMidnight(new Date()).slice(0, 10));
  const [toDate, setToDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 14);
    return isoMidnight(d).slice(0, 10);
  });

  const reload = async () => {
    setLoading(true);
    try {
      const from = `${fromDate}T00:00:00Z`;
      const to = `${toDate}T00:00:00Z`;
      const { data } = await api.get(
        `/calendar/hosts/${hostSlug}/slots`,
        { params: { meeting_type_id: mtId, from, to, invitee_tz: tz } },
      );
      setSlots(data.slots || []);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error(t("calendarMVP.book.invalidHost"));
      } else {
        toast.error(extractErrorMessage(err, t("apiFallback.serverError")));
      }
      setSlots([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostSlug, mtId, fromDate, toDate]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!picked) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/calendar/hosts/${hostSlug}/bookings`, {
        meeting_type_id: mtId,
        invitee_email: form.email,
        invitee_name: form.name,
        slot_start_utc: picked.starts_at,
        slot_end_utc: picked.ends_at,
        invitee_tz: tz,
        language: (navigator.language || "en").toLowerCase().slice(0, 2),
        notes: form.notes || null,
        honeypot: "",
      });
      setConfirmed(data);
    } catch (err) {
      toast.error(extractErrorMessage(err, t("apiFallback.serverError")));
    } finally { setSubmitting(false); }
  };

  if (confirmed) {
    const deepLink = `${window.location.origin}/booking/${confirmed.confirmation_token}`;
    return (
      <PublicLayout>
        <div className="mx-auto max-w-2xl px-4 py-12" data-testid="booking-success">
          <Card className="overflow-hidden border-emerald-200">
            <div className="bg-emerald-50 px-6 py-4 border-b border-emerald-200 flex items-center gap-3">
              <CalendarCheck className="h-6 w-6 text-emerald-600" />
              <h1 className="text-xl font-semibold text-slate-900">{t("calendarMVP.book.successTitle")}</h1>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-slate-700">{t("calendarMVP.book.successBody")}</p>
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 space-y-1">
                <div className="font-medium text-slate-900">{confirmed.meeting_type_name_snapshot}</div>
                <div className="text-sm text-slate-700">{fmtLocal(confirmed.slot_start_utc, tz)}</div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to={`/booking/${confirmed.confirmation_token}`}
                  className="inline-flex items-center justify-center rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 font-medium"
                  data-testid="booking-success-deep-link"
                >
                  {t("calendarMVP.book.successLink")}
                </Link>
                <Button
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(deepLink);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  data-testid="booking-success-copy"
                >
                  {copied ? t("calendarMVP.book.successCopied") : t("calendarMVP.book.successCopy")}
                </Button>
              </div>
              <Link to="/" className="block text-sm text-slate-500 hover:text-slate-700 mt-4">
                ← {t("calendarMVP.book.backHome")}
              </Link>
            </div>
          </Card>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="mx-auto max-w-3xl px-4 py-12" data-testid="public-booking-page">
        <h1 className="text-3xl font-semibold text-slate-900 mb-6" data-testid="book-title">
          {t("calendarMVP.book.title")}
        </h1>

        <Card className="p-5 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <div>
              <Label htmlFor="from">{t("calendarMVP.book.fromLabel")}</Label>
              <Input id="from" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="book-from-input" />
            </div>
            <div>
              <Label htmlFor="to">{t("calendarMVP.book.toLabel")}</Label>
              <Input id="to" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="book-to-input" />
            </div>
            <div>
              <Label>{t("calendarMVP.book.tzLabel")}</Label>
              <div className="text-sm text-slate-700 mt-1 px-3 py-2 bg-slate-50 rounded">
                {tz} <span className="text-slate-400 text-xs ms-1">({t("calendarMVP.book.tzAuto")})</span>
              </div>
            </div>
          </div>
        </Card>

        <h2 className="text-lg font-semibold text-slate-900 mb-3">{t("calendarMVP.book.pickSlot")}</h2>
        {loading ? (
          <div className="text-center py-8" data-testid="book-slots-loading">
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-emerald-600" />
            <p className="mt-2 text-sm text-slate-500">{t("calendarMVP.book.loading")}</p>
          </div>
        ) : slots.length === 0 ? (
          <p className="text-slate-500 italic" data-testid="book-slots-empty">{t("calendarMVP.book.noSlots")}</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-8" data-testid="book-slots-grid">
            {slots.map((s) => {
              const active = picked?.starts_at === s.starts_at;
              const localStart = s.invitee_local
                ? `${s.invitee_local.start_time}`
                : new Intl.DateTimeFormat(undefined, { timeZone: tz, timeStyle: "short" }).format(new Date(s.starts_at));
              const dayLabel = s.invitee_local
                ? s.invitee_local.date
                : new Intl.DateTimeFormat(undefined, { timeZone: tz, month: "short", day: "numeric" }).format(new Date(s.starts_at));
              return (
                <button
                  key={s.starts_at}
                  type="button"
                  onClick={() => setPicked(s)}
                  className={`rounded-lg border px-3 py-2 text-left transition ${
                    active
                      ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-300"
                      : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                  data-testid={`book-slot-${s.starts_at}`}
                >
                  <div className="text-xs uppercase text-slate-500">{dayLabel}</div>
                  <div className="text-base font-semibold text-slate-900">{localStart}</div>
                  {s.invitee_local && (
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      {t("calendarMVP.book.inHostTz").replace("{time}", s.host_local.start_time)}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {picked && (
          <Card className="p-5" data-testid="book-form-card">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">{t("calendarMVP.book.yourDetails")}</h2>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="name">{t("calendarMVP.book.nameLabel")} *</Label>
                <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="book-name-input" />
              </div>
              <div>
                <Label htmlFor="email">{t("calendarMVP.book.emailLabel")} *</Label>
                <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="book-email-input" />
              </div>
              <div>
                <Label htmlFor="notes">{t("calendarMVP.book.notesLabel")}</Label>
                <Textarea
                  id="notes" maxLength={1000}
                  placeholder={t("calendarMVP.book.notesPlaceholder")}
                  value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="book-notes-input"
                />
              </div>
              <Button
                type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="book-submit-btn"
              >
                {submitting ? t("calendarMVP.book.submitting") : t("calendarMVP.book.cta")}
              </Button>
            </form>
          </Card>
        )}
      </div>
    </PublicLayout>
  );
}
